import { createSelector } from '@reduxjs/toolkit';
import { configService } from 'Services';

export const configSelector = (state: ReduxState) => state.configReducer;
export const appInfoSelector = (state: ReduxState) => configSelector(state)?.appInfo;
export const getAppUrls = (state: ReduxState) => configSelector(state)?.appInfo?.appUrls ?? [];
export const globalRulesSelector = (state: ReduxState) => configSelector(state)?.data?.globalRules;
export const overridesSelector = (state: ReduxState) => configSelector(state)?.data?.overrides;

export const selectGlobalRulesBulkUploadConfig = createSelector(
    globalRulesSelector,
    (config) => config?.globalBulkUpload
);

export const selectMerchantOverridesBulkUploadConfig = createSelector(
    overridesSelector,
    (config) => config?.bulkUpload?.merchant
);

export const commonVarsSelector = (state: ReduxState) => configSelector(state)?.data?.common;

// feature flag config selectors
// When featureflags are missing or configService is failing, we want to hide it from users
// Otherwise, return values of featureFlags from ConfigService
export const shouldShowLists = () => configService.featureFlags.listManagement || false;
export const shouldShowMerchants = () => configService.featureFlags.showAllMerchantRelated || false;
export const shouldShowManageTenants = (landlordId?: string, tenantKey?: string) => {
    return (landlordId && landlordId === tenantKey && configService.featureFlags.showManageTenants) || false;
};

export const shouldShowParameters = () => configService.featureFlags.manageParameters || false;
export const shouldShowManageVelocities = () => configService.featureFlags.manageVelocities || false;

export const shouldShowBulkUploads = () => configService.featureFlags.bulkUploads || false;
export const shouldShowGlobalRules = () => configService.featureFlags.globalRules || false;
export const shouldShowReports = () => configService.featureFlags.reports || false;
export const shouldShowRevisions = () => configService.featureFlags.revisions || false;
export const shouldShowSimulations = () => configService.featureFlags.simulations || false;
export const shouldShowCompareSimulations = () => configService.featureFlags.compareSimulations || false;
export const shouldShowCreateRule = () => configService.featureFlags.createRule || false;
export const shouldShowPanList = () => configService.featureFlags.panList || false;
export const shouldShowDeploy = () => configService.featureFlags.demo || false;
export const shouldShowExecutionOrder = () => configService.featureFlags.executionOrder || false;
export const shouldShowAdvancedRules = () => configService.featureFlags.showAdvancedRule || false;
export const shouldShowSetFlags = () => configService.featureFlags.setFlags || false;
export const shouldShowAddToList = () => configService.featureFlags.addToList || false;
export const shouldShowTenantGroups = () => configService.featureFlags.tenantGroups || false;
export const shouldShowNetworkRules = () => configService.featureFlags.networkRules || false;
export const shouldShowReviewConclusions = () => configService.featureFlags.reviewConclusions || false;
export const shouldShowCurrentDate = () => configService.featureFlags.simulationCurrentDate || false;
export const shouldShowRangeInListTypes = () => configService.featureFlags.rangeListType || false;
export const shouldShowScheduledDeployment = () => configService.featureFlags.scheduledDeployment || false;
export const shouldShowTableauReports = () => configService.featureFlags.tableauEnabled || false;
export const shouldShowRuleTester = () => configService.featureFlags.ruleTester || false;
export const shouldRuleGroupConditions = () => configService.featureFlags.ruleGroupConditions || false;
export const shouldDeleteRule = () => configService.featureFlags.deleteRule ?? false;
export const shouldShowListImportExport = () => configService.featureFlags.listImportExport ?? false;
export const shouldShowRuleTests = () => configService.featureFlags.ruleTests ?? false;
/** This feature flag should be removed when all customers are migrated to Tableau */
export const shouldDisableLegacyReports = () => configService.featureFlags.disableLegacyReports ?? false;
